import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import classNames from "classnames";
import InfoIcon from "@mui/icons-material/Info";
import CancelIcon from "@mui/icons-material/Cancel";
import moment from "moment-timezone";

import "../../assets/transactions.scss";
import {
  bitCoinSymbol,
  ethereumIcon,
  transactionIcons,
  tronIcon,
  solanaIcon,
  currencyImages,
  lightningIcons,
  tonIcon,
} from "../images";
import Text from "../text/Text";
import {
  btcToSatsAmount,
  callAPIInterface,
  dateTimeFormat,
  displayTaprootCurrency,
  getAmountFormatted,
  getCurrencyAmount,
  getDisplayAmount,
  getExchangeRate,
  getPaidPaymentMethod,
  lightningLabel,
  onChainLabel,
  ethereumLabel,
  tronLabel,
  solanaLabel,
  displayAmountWithCurrency,
  tonLabel,
  getDisplayCurrencySymbol,
} from "../constants";
import {
  amountTxt,
  completed,
  confirmationCount,
  invoice,
  notes,
  pending,
  received,
  status,
  when,
  networkFee,
  address,
  sent,
  receiving,
  paidTo,
  sending,
  routingFee,
  viewInMemPool,
  speedFee,
  gasFee,
  sats,
  viewInEtherScan,
  id,
  speedFeeMsg,
  networkFeeMsg,
  routingFeeMsg,
  gasFeeMsg,
  paymentPreImage,
  failed,
  maxSettleMentTimeTxt,
  maxSettleMentTimeTxtInfo,
  settledBy,
  viewInTronScan,
  swapFrom,
  swapTo,
  btc,
  conversionFeeLabel,
  baseFee,
  baseFeeMsg,
  viewInSolScan,
  conversionFeeInfoMsg,
  transactionDetails,
  viewInTonScan,
} from "../messages";
import TransactionDetailSkeleton from "../TransactionDetailSkeleton";
import Clipboard from "../clipboard/Clipboard";
import { CustomLink } from "../link/Link";
import WalletSymbols from "../WalletSymbols";
import { CustomPopper } from "../popper/Popper";
import CommonHeader from "../CommonHeader";
import { useSession } from "../../hooks/useSession";

const commonMethodArr = [
  onChainLabel,
  ethereumLabel,
  tronLabel,
  solanaLabel,
  tonLabel,
];

function TransactionDetail() {
  const navigate = useNavigate();
  const { walletCurrency, showBtcAmount } = useSelector(
    (state) => state.common
  );

  const { code: targetedCurrency, symbol: targetedCurrencySymbol } = {
    ...walletCurrency,
  };
  const { session } = useSession();

  const [loader, setLoader] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const [exchangeRate, setExchangeRate] = useState({});
  const [amount, setAmount] = useState(0);
  const [infoMsg, setInfoMsg] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [currency, setCurrency] = useState(null);
  const [mainAmount, setMainAmount] = useState(0);
  const [maxSettleMentTime, setMaxSettleMentTime] = useState(null);

  const open = Boolean(anchorEl);

  const currencyColor = classNames("bg-color-" + currency);

  const iconCss = {
    height: "24px",
    width: "24px",
    color: "#2A67FF",
    marginLeft: "4px",
  };

  const isFromPayment = transaction?.object === "payment";
  const transactionDetail = JSON.parse(
    localStorage.getItem("transactionDetail")
  );
  const paymentMethod = getPaidPaymentMethod(transaction);
  const isWithdraw =
    transactionDetail?.type?.toLowerCase() === "withdraw" ||
    transactionDetail?.type?.toLowerCase() === "instant_send";

  const isPayment = transactionDetail?.type?.toLowerCase() === "payment";
  const isSwap =
    transactionDetail?.type?.toLowerCase() === "swap in" ||
    transactionDetail?.type?.toLowerCase() === "swap out";
  const isSwapIn = transactionDetail?.type?.toLowerCase() === "swap in";
  const isSwapOut = transactionDetail?.type?.toLowerCase() === "swap out";
  const userDateFormat = session?.user?.date_format || "DD/MM/YYYY";

  useEffect(() => {
    window.scrollTo(0, 0);
    if (transactionDetail?.type?.toLowerCase() === "payment") {
      fetchPaymentDetail(transactionDetail?.id);
    } else if (isWithdraw) {
      fetchWithdrawDetail(transactionDetail?.id);
    } else if (isSwap) {
      fetchSwapDetail(transactionDetail?.id);
    } else {
      navigate("/history");
    }

    return () => {
      localStorage.removeItem("transaction");
    };
  }, []);

  useEffect(() => {
    if (
      paymentMethod === lightningLabel &&
      !transactionDetail?.confirmed &&
      isWithdraw
    ) {
      handleDecode(transaction?.withdraw_request);
    }
  }, [transaction]);

  const handleDecode = async (address) => {
    try {
      const response = await callAPIInterface("POST", "/withdraws/decode", {
        withdraw_request: address,
      });
      const expireTime = response?.expires_at * 1000;
      setMaxSettleMentTime(
        expireTime > new Date().getTime() ? expireTime : null
      );
    } catch (err) {}
  };

  const fetchExchangeRate = async (currency) => {
    try {
      const res = await getExchangeRate(targetedCurrency, currency);
      setExchangeRate(res);
    } catch (error) {
      console.error("Error fetching exchange rate:", error);
    }
  };

  const displayAmountInFiatCurrency = (amount) => {
    const fiatAmount = amount / exchangeRate?.target_highest_rate;
    const displayAmount = getDisplayAmount(true, fiatAmount, targetedCurrency);

    return `${targetedCurrencySymbol}${displayAmount}`;
  };

  useEffect(() => {
    if (transaction) {
      let amount, calculateAmount;
      const speedFee = transaction?.speed_fee?.total_fee;

      if (isWithdraw) {
        amount = transaction?.target_amount;
        calculateAmount = amount + speedFee;
      } else if (isPayment) {
        amount = transaction?.target_amount_paid;
        calculateAmount = amount - speedFee;
      } else if (isSwapIn) {
        amount = transaction?.target_amount_swap_in;
        calculateAmount = amount - speedFee;
      } else if (isSwapOut) {
        amount = transaction?.target_amount_swap_out;
        calculateAmount = transaction?.target_amount_swap_out;
      }

      setMainAmount(getCurrencyAmount(calculateAmount, currency));

      setAmount(amount);
    }
  }, [transaction]);

  const fetchPaymentDetail = (payment_id) => {
    callAPIInterface("GET", `/payments/${payment_id}`)
      .then((res) => {
        setLoader(false);
        setTransaction(res);
        setCurrency(res?.target_currency);
        fetchExchangeRate(res?.target_currency);
      })
      .catch((e) => {
        setLoader(false);
      });
  };

  const fetchWithdrawDetail = (id) => {
    callAPIInterface("GET", `/withdraws/${id}`)
      .then((res) => {
        setLoader(false);
        setTransaction(res);
        setCurrency(res?.target_currency);
        fetchExchangeRate(res?.target_currency);
      })
      .catch((e) => {
        setLoader(false);
      });
  };

  const fetchSwapDetail = async (id) => {
    try {
      const response = await callAPIInterface("GET", `/balances/swap/${id}`);
      setLoader(false);
      setTransaction(response);
      setCurrency(
        isSwapIn
          ? response?.target_currency_swap_in
          : response?.target_currency_swap_out
      );
      fetchExchangeRate(
        isSwapIn
          ? response?.target_currency_swap_in
          : response?.target_currency_swap_out
      );
    } catch (err) {
      setLoader(false);
    }
  };

  const displayDetail = ({
    label,
    value,
    textColor,
    showRedirectIcon = false,
    showCopyIcon = false,
    icon = null,
    linkLabel = "",
    showInfoIcon = false,
  }) => {
    let infoIconMsg, infoLabel;
    if (label === speedFee) {
      infoIconMsg = speedFeeMsg;
      infoLabel = speedFee;
    } else if (label === maxSettleMentTimeTxt) {
      infoIconMsg = maxSettleMentTimeTxtInfo;
      infoLabel = maxSettleMentTimeTxt;
    } else if (label === conversionFeeLabel) {
      infoIconMsg = conversionFeeInfoMsg;
      infoLabel = conversionFeeLabel;
    } else {
      infoIconMsg = labelInfo?.infoMsg;
      infoLabel = labelInfo?.feeLabel;
    }

    return (
      <Box mb="15px" marginLeft="10px">
        <Text
          className="grey-text"
          size={16}
          font="regular"
          sx={{ display: "flex", alignItems: "center" }}
        >
          {label}
          {showInfoIcon && displayInfoIcon(infoIconMsg, infoLabel)}
        </Text>
        <Box display="flex" justifyContent="space-between">
          <Text
            className="default-text"
            size={16}
            font="regular"
            sx={{
              display: "flex",
              alignItems: "center",
              ...(textColor && { color: textColor }),
              ...(showCopyIcon && {
                justifyContent: "space-between",
                alignItems: "baseline",
                width: "88%",
              }),
            }}
            breakAll
          >
            {icon}
            {value || "-"}
          </Text>
          <Box display="flex">{showCopyIcon && <Clipboard text={value} />}</Box>
        </Box>
        <Box display="flex">
          {showRedirectIcon && transaction?.on_chain_tx_explorer_link && (
            <CustomLink
              href={transaction?.on_chain_tx_explorer_link}
              target="_blank"
              sx={{
                marginRight: "5px",
                color: "#2A67FF",
                textTransform: "uppercase",
                marginTop: "10px",
              }}
              bold
              alignIcon="end"
              withIcon={<WalletSymbols sx={iconCss} symbol="open_in_new" />}
            >
              {linkLabel}
            </CustomLink>
          )}
        </Box>
      </Box>
    );
  };
  const transactionType = () => {
    if (!transactionDetail?.confirmed) {
      return isFromPayment ? receiving : sending;
    } else {
      if (isFromPayment) {
        return received;
      } else if (isWithdraw) {
        return sent;
      } else if (isSwap) {
        return transactionDetail?.type;
      }
    }
  };

  const renderStatusIcon = () => {
    return transactionDetail?.confirmed ? (
      <CheckCircleIcon
        sx={{
          color: "#0CAD62 !important",
          height: "20px",
          width: "20px",
          marginRight: "8px",
          marginLeft: "0px !important",
        }}
      />
    ) : (
      <ErrorIcon
        sx={{
          color: "#D88A2C !important",
          height: "20px",
          width: "20px",
          marginRight: "8px",
          marginLeft: "0px !important",
        }}
      />
    );
  };

  const displayInfoIcon = (msg = null, label = null) => {
    return (
      <>
        <InfoIcon
          style={{
            height: 20,
            width: 20,
            color: "#848b9e",
            cursor: "pointer",
          }}
          onClick={(e) => {
            setInfoMsg(msg || labelInfo?.infoMsg);
            setAnchorEl(anchorEl ? null : e.currentTarget);
          }}
        />
        <CustomPopper
          id={label || labelInfo?.addressLabel}
          disablePortal={true}
          open={open}
          anchorEl={anchorEl}
          position="top"
          handleClose={() => setAnchorEl(null)}
          sx={{ width: "332px" }}
        >
          <Text variant="inherit" font="regular" size={14}>
            {infoMsg}
          </Text>
        </CustomPopper>
      </>
    );
  };
  const methodIconAndAddress = () => {
    let icon, address;
    switch (paymentMethod) {
      case lightningLabel:
        icon = lightningIcons[currency];
        address =
          transaction?.payment_method_options?.lightning?.payment_request;
        break;
      case onChainLabel:
        icon = bitCoinSymbol;
        address = transaction?.payment_method_options?.on_chain?.address;
        break;
      case ethereumLabel:
        icon = ethereumIcon;
        address = transaction?.payment_method_options?.ethereum?.address;
        break;
      case tronLabel:
        icon = tronIcon;
        address = transaction?.payment_method_options?.tron?.address;
        break;
      case solanaLabel:
        icon = solanaIcon;
        address = transaction?.payment_method_options?.solana?.address;
        break;
      case tonLabel:
        icon = tonIcon;
        address = transaction?.payment_method_options?.ton?.address;
        break;
      default:
        if (isSwapOut) {
          icon = currencyImages[transaction?.target_currency_swap_out];
        } else if (isSwapIn) {
          icon = currencyImages[transaction?.target_currency_swap_in];
        } else {
          icon = bitCoinSymbol;
          address = "-";
        }
    }
    return { icon, address };
  };
  const labelInfo = useMemo(() => {
    let feeLabel = "";
    let addressLabel = "";
    let infoMsg = "";

    if (paymentMethod === onChainLabel || paymentMethod === tronLabel) {
      feeLabel = networkFee;
      addressLabel = address;
      infoMsg = networkFeeMsg;
    } else if (paymentMethod === lightningLabel) {
      feeLabel = routingFee;
      addressLabel = invoice;
      infoMsg = routingFeeMsg;
    } else if (paymentMethod === ethereumLabel) {
      feeLabel = gasFee;
      addressLabel = address;
      infoMsg = gasFeeMsg;
    } else if (paymentMethod === solanaLabel || paymentMethod === tonLabel) {
      feeLabel = baseFee;
      addressLabel = address;
      infoMsg = baseFeeMsg;
    }

    return { feeLabel, addressLabel, infoMsg };
  }, [transaction]);

  const statusInfo = useMemo(() => {
    let value, color, icon;
    if (transaction?.status === "failed") {
      value = failed;
      color = "#e74c3c !important";
      icon = (
        <CancelIcon
          sx={{
            color: "#e74c3c !important",
            height: "20px",
            width: "20px",
            marginRight: "8px",
            marginLeft: "0px !important",
          }}
        />
      );
    } else {
      const isCompleted =
        transaction?.status === "completed" || transaction?.status === "paid";
      value = isCompleted ? completed : pending;
      color = isCompleted ? "#0CAD62 !important" : "#D88A2C !important";
      icon = isCompleted ? (
        <CheckCircleIcon
          sx={{
            color: "#0CAD62 !important",
            height: "20px",
            width: "20px",
            marginRight: "8px",
            marginLeft: "0px !important",
          }}
        />
      ) : (
        <ErrorIcon
          sx={{
            color: "#D88A2C !important",
            height: "20px",
            width: "20px",
            marginRight: "8px",
            marginLeft: "0px !important",
          }}
        />
      );
    }
    return { value, color, icon };
  }, [transaction]);

  const scanLabel = useMemo(() => {
    let name = "";
    if (paymentMethod === onChainLabel) {
      name = viewInMemPool;
    } else if (paymentMethod === ethereumLabel) {
      name = viewInEtherScan;
    } else if (paymentMethod === tronLabel) {
      name = viewInTronScan;
    } else if (paymentMethod === solanaLabel) {
      name = viewInSolScan;
    } else if (paymentMethod === tonLabel) {
      name = viewInTonScan;
    }
    return { name };
  }, [paymentMethod]);

  const displayDetailPageAmount = (amount) => {
    const currencyAmount = getCurrencyAmount(amount, currency);
    const amountToConvertFiat =
      showBtcAmount && currency === sats
        ? btcToSatsAmount(currencyAmount)
        : currencyAmount;
    const displayCurrency = showBtcAmount && currency === sats ? btc : currency;
    const formattedAmt = getAmountFormatted(currencyAmount, currency);

    return (
      <>
        {displayAmountWithCurrency(displayCurrency, formattedAmt)} {"(~ "}
        {displayAmountInFiatCurrency(amountToConvertFiat)}
        {")"}
      </>
    );
  };

  const currencyInfo = useMemo(() => {
    let sign, color;
    if (isFromPayment || isSwapIn) {
      sign = "+";
      color = "#0CAD62 !important";
    } else if (isWithdraw || isSwapOut) {
      sign = "-";
      color = "#FD371E !important";
    }
    return { sign, color };
  }, [transactionDetail]);

  return loader ? (
    <TransactionDetailSkeleton />
  ) : (
    <>
      <CommonHeader textAfterIcon={transactionDetails} icon="backArrow" />
      <Box className="options-container">
        <Box className="all-transaction-wrapper">
          <Box
            className={`transaction-container all-transactions transaction-detail`}
          >
            <Box
              className={`amount-section ${currencyColor}`}
              alignItems="center"
              justifyContent="center"
            >
              <img src={methodIconAndAddress()?.icon} height={50} width={50} />

              <Text className="mt-16" withIcon="end" size={20} font="semibold">
                {transactionType()}
              </Text>
              <Text
                className="default-text amount"
                size={34}
                font="semibold"
                sx={{
                  color: currencyInfo?.color,
                }}
              >
                {mainAmount < 0
                  ? "-"
                  : mainAmount > 0
                    ? currencyInfo?.sign
                    : ""}

                {getDisplayCurrencySymbol(currency, showBtcAmount)}

                {getAmountFormatted(Math.abs(mainAmount), currency)}
              </Text>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <img
                  src={currencyImages[currency?.toUpperCase()]}
                  alt={currency}
                  style={{ width: "20px", height: "20px" }}
                />

                <Text className="grey-text" size={20} font="regular">
                  {displayTaprootCurrency(amount, currency)}
                </Text>
              </Box>
              {maxSettleMentTime && (
                <Box className="transaction-with-symbol">
                  {transactionIcons["unconfirmed"]}
                  <Text className="grey-text ml-4" size={14} font="semibold">
                    {`${settledBy} ${dateTimeFormat(
                      maxSettleMentTime,
                      userDateFormat
                    )}`}
                  </Text>
                </Box>
              )}
            </Box>
            <Box className="detail-section">
              <Box>
                {displayDetail({
                  label: when,
                  value: dateTimeFormat(
                    transaction?.target_amount_paid_at || transaction?.created,
                    userDateFormat
                  ),
                })}
                {displayDetail({
                  label: status,
                  value: statusInfo?.value,
                  textColor: statusInfo?.color,
                  icon: statusInfo?.icon,
                })}

                {transaction?.target_currency_swap_out &&
                  displayDetail({
                    label: swapFrom,
                    value:
                      showBtcAmount &&
                      transaction?.target_currency_swap_out === sats
                        ? btc
                        : transaction?.target_currency_swap_out,
                  })}

                {transaction?.target_currency_swap_in &&
                  displayDetail({
                    label: swapTo,
                    value:
                      showBtcAmount &&
                      transaction?.target_currency_swap_in === sats
                        ? btc
                        : transaction?.target_currency_swap_in,
                  })}

                {displayDetail({
                  label: amountTxt,
                  value: displayDetailPageAmount(amount),
                })}

                <>
                  {!isSwapOut &&
                    displayDetail({
                      label: speedFee,
                      value: displayDetailPageAmount(
                        transaction?.speed_fee?.total_fee
                      ),

                      showInfoIcon: true,
                    })}
                </>

                {isWithdraw &&
                  transaction?.ln_pay?.payee &&
                  displayDetail({
                    label: paidTo,
                    value: `${transaction?.ln_pay?.payee}`,
                  })}
                {transaction?.statement_descriptor &&
                  displayDetail({
                    label: notes,
                    value: transaction?.statement_descriptor,
                  })}
              </Box>
              <Box>
                {maxSettleMentTime &&
                  displayDetail({
                    label: maxSettleMentTimeTxt,
                    value: moment(maxSettleMentTime).fromNow(true),
                    showInfoIcon: true,
                    icon: "~",
                  })}
                {transactionDetail?.type?.toLowerCase() === "payment" &&
                  commonMethodArr.includes(paymentMethod) &&
                  displayDetail({
                    label: confirmationCount,
                    value: `${transaction?.confirmations}${
                      transaction?.confirmations === 1 ? "+" : ""
                    }`,
                  })}
                {isWithdraw &&
                  displayDetail({
                    label: labelInfo?.feeLabel,
                    value: displayDetailPageAmount(transaction?.est_fees),
                    showInfoIcon: true,
                  })}

                {paymentMethod === lightningLabel &&
                  isWithdraw &&
                  displayDetail({
                    label: conversionFeeLabel,
                    value: displayDetailPageAmount(transaction?.conversion_fee),
                    showInfoIcon: true,
                  })}
              </Box>
            </Box>
            {labelInfo?.addressLabel &&
              displayDetail({
                label: labelInfo?.addressLabel,
                value: isWithdraw
                  ? transaction?.withdraw_request
                  : methodIconAndAddress()?.address,
                showCopyIcon: true,
              })}

            {transaction?.payment_preimage &&
              displayDetail({
                label: paymentPreImage,
                value: transaction?.payment_preimage,
              })}
            {displayDetail({
              label: id,
              value: transaction?.id,
              showRedirectIcon: commonMethodArr.includes(paymentMethod),
              linkLabel: scanLabel?.name,
            })}
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default TransactionDetail;
